import React, { useState } from 'react';
import { Crown, Trash2, Users, Clock } from 'lucide-react';
import * as miniLeaguesApi from '../../api/miniLeagues';

/**
 * League roster with manager-only removal.
 *
 * Removing someone from the league never pulls them out of a game they are
 * already in - in any game state. Whenever they're on a current game's roster
 * the backend answers PENDING_REMOVAL rather than REMOVED, meaning they play
 * that game out and drop at the next cycle. That distinction is surfaced rather
 * than hidden, otherwise a manager sees "removed" and is confused when they
 * keep playing.
 *
 * To take someone out of a game that hasn't finished, the manager uses the
 * remove control on the game itself, which is allowed until the first results
 * are in.
 */
function LeagueMembers({ members, currentUser, isManager, leagueId, onChanged }) {
  const [busyPlayerId, setBusyPlayerId] = useState(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const handleRemove = async (member) => {
    if (
      !window.confirm(
        `Remove ${member.playerName} from this league?\n\nThey'll finish any game they're already in.`
      )
    ) {
      return;
    }
    setError('');
    setNotice('');
    setBusyPlayerId(member.playerId);
    try {
      const result = await miniLeaguesApi.removeMember(leagueId, member.playerId);
      if (result && result.status === 'PENDING_REMOVAL') {
        setNotice(
          `${member.playerName} is in a current game, so they'll finish it and leave when the next one starts. To take them out of that game now, use the game itself.`
        );
      } else {
        setNotice(`${member.playerName} has been removed from the league.`);
      }
      if (onChanged) onChanged();
    } catch (err) {
      if (err.name === 'ApiError') {
        setError(err.body?.error || 'Failed to remove member');
      } else {
        setError('Error removing member');
      }
    } finally {
      setBusyPlayerId(null);
    }
  };

  if (!members || members.length === 0) {
    return (
      <div className="empty-state">
        <Users size={56} />
        <h3>No Members Yet</h3>
        <p>Share the league code above to get your mates in</p>
      </div>
    );
  }

  return (
    <>
      {notice && <div className="league-notice">{notice}</div>}
      {error && <div className="error-message">{error}</div>}

      <div className="member-list">
        {members.map((member) => {
          const isYou = String(member.playerId) === String(currentUser?.id);
          const pending = member.status === 'PENDING_REMOVAL';
          const canRemove = isManager && !member.manager && !isYou;

          return (
            <div key={member.playerId} className={pending ? 'member-row pending' : 'member-row'}>
              <div className="member-identity">
                <span className="member-name">{member.playerName}</span>
                {member.manager && <Crown size={14} className="member-crown" title="League manager" />}
                {isYou && <span className="lt-you-tag">You</span>}
              </div>

              <div className="member-meta">
                {pending && (
                  <span className="member-status" title="Finishing the current game before leaving">
                    <Clock size={13} />
                    Leaving after this game
                  </span>
                )}
                {canRemove && (
                  <button
                    className="remove-player-btn"
                    onClick={() => handleRemove(member)}
                    disabled={busyPlayerId === member.playerId}
                    title={`Remove ${member.playerName}`}
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {isManager && (
        <p className="helper-text">
          Removing someone here takes them out of the league, not out of a game
          they're already in — they'll finish that game and drop out when the next
          one starts. To take someone out of a game now, use the game itself.
        </p>
      )}
    </>
  );
}

export default LeagueMembers;
